import { useState, useEffect } from "react";
import api from "../../services/api";
import toast from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import DataTable from "../components/DataTable";
import { Plus, X, Save } from "lucide-react";

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await api.get("/categories", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      toast.error("Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const openAddModal = () => {
    setEditMode(false);
    setCategoryName("");
    setIsModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditMode(true);
    setCurrentId(category.id);
    setCategoryName(category.name);
    setIsModalOpen(true);
  };

  const handleDelete = async (category) => {
    if (window.confirm(`Delete category "${category.name}"?`)) {
      try {
        const token = localStorage.getItem("admin_token");
        await api.delete(`/categories/${category.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Category deleted!");
        setCategories(categories.filter(c => c.id !== category.id));
      } catch (error) {
        toast.error("Failed to delete category.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) return toast.error("Category name is required");
    
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      
      if (editMode) {
        await api.put(`/categories/${currentId}`, { name: categoryName }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Category updated!");
      } else {
        await api.post("/categories", { name: categoryName }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Category added!");
      }
      
      setIsModalOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong!");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "name", label: "Category Name" }
  ];

  return (
    <AdminLayout>
      <div className="mb-8 flex items-center justify-between">
        <PageTitle title="Job Categories" subtitle="Manage categories for job listings." />
        <button onClick={openAddModal} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-6 rounded-lg transition-colors">
          <Plus size={18} /> Add Category
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center h-64 items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <DataTable
          title="All Categories"
          columns={columns}
          data={categories}
          onEdit={openEditModal}
          onDelete={handleDelete}
        />
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50">
              <h3 className="font-bold text-lg text-gray-800">{editMode ? "Edit Category" : "Add New Category"}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
              <input 
                type="text" autoFocus required
                value={categoryName} 
                onChange={(e) => setCategoryName(e.target.value)} 
                placeholder="e.g. IT & Software" 
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500 mb-6" 
              />
              
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2.5 font-semibold rounded-lg transition-colors disabled:opacity-50">
                  <Save size={18} /> {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}